import { proxyCustomElement, HTMLElement, createEvent, h, Fragment, Host } from '@stencil/core/internal/client';
import { m as menuListConfig, i as isEqual_1 } from './menuListConfig.js';
import { j as transformToVersionedTag, m as autoFocusElement, b as isEventTargetContained } from './utils.js';
import { F as FOCUS_TYPE } from './common.js';
import { Z as Z_INDEX } from './consts.js';
import { d as defineCustomElement$k } from './wpp-checkbox2.js';
import { d as defineCustomElement$j } from './wpp-divider2.js';
import { d as defineCustomElement$i } from './wpp-icon-chevron2.js';
import { d as defineCustomElement$h } from './wpp-icon-cross2.js';
import { d as defineCustomElement$g } from './wpp-icon-dash2.js';
import { d as defineCustomElement$f } from './wpp-icon-error2.js';
import { d as defineCustomElement$e } from './wpp-icon-info2.js';
import { d as defineCustomElement$d } from './wpp-icon-search2.js';
import { d as defineCustomElement$c } from './wpp-icon-success2.js';
import { d as defineCustomElement$b } from './wpp-icon-tick2.js';
import { d as defineCustomElement$a } from './wpp-icon-warning2.js';
import { d as defineCustomElement$9 } from './wpp-inline-message2.js';
import { d as defineCustomElement$8 } from './wpp-internal-label2.js';
import { d as defineCustomElement$7 } from './wpp-internal-tooltip2.js';
import { d as defineCustomElement$6 } from './wpp-label2.js';
import { d as defineCustomElement$5 } from './wpp-list-item2.js';
import { d as defineCustomElement$4 } from './wpp-spinner2.js';
import { d as defineCustomElement$3 } from './wpp-tooltip2.js';
import { d as defineCustomElement$2 } from './wpp-typography2.js';

const BLUR_TIME = 250;
const DROPDOWN_ANIMATION_TIME = [300, BLUR_TIME];

const getTempNodeWidthBasedOnLabel = (textStyles, label) => {
  const tmp = document.createElement('span');
  const textNode = document.createTextNode('');
  tmp.appendChild(textNode);
  document.body.appendChild(tmp);
  tmp.style.cssText = textStyles;
  tmp.style.opacity = '0';
  tmp.style.position = 'absolute';
  tmp.style.width = 'auto';
  tmp.style.overflow = 'scroll';
  tmp.style.whiteSpace = 'no-wrap';
  tmp.innerText = label;
  const nodeWidth = tmp.getBoundingClientRect().width;
  document.body.removeChild(tmp);
  return nodeWidth;
};

const wppAutocompleteCss = ".typography-5xl-display{font-size:var(--wpp-typography-5xl-display-font-size, 48px);font-family:var(--wpp-typography-5xl-display-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-5xl-display-line-height, 62px);letter-spacing:var(--wpp-typography-5xl-display-letter-spacing, 0);text-transform:var(--wpp-typography-5xl-display-text-transform, initial);color:var(--wpp-typography-5xl-display-color, var(--wpp-text-color));font-weight:var(--wpp-typography-5xl-display-font-weight, 600);font-style:var(--wpp-typography-5xl-display-font-style, normal)}.typography-4xl-display{font-size:var(--wpp-typography-4xl-display-font-size, 36px);font-family:var(--wpp-typography-4xl-display-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-4xl-display-line-height, 48px);letter-spacing:var(--wpp-typography-4xl-display-letter-spacing, 0);text-transform:var(--wpp-typography-4xl-display-text-transform, initial);color:var(--wpp-typography-4xl-display-color, var(--wpp-text-color));font-weight:var(--wpp-typography-4xl-display-font-weight, 600);font-style:var(--wpp-typography-4xl-display-font-style, normal)}.typography-3xl-heading{font-size:var(--wpp-typography-3xl-heading-font-size, 28px);font-family:var(--wpp-typography-3xl-heading-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-3xl-heading-line-height, 40px);letter-spacing:var(--wpp-typography-3xl-heading-letter-spacing, 0);text-transform:var(--wpp-typography-3xl-heading-text-transform, initial);color:var(--wpp-typography-3xl-heading-color, var(--wpp-text-color));font-weight:var(--wpp-typography-3xl-heading-font-weight, 600);font-style:var(--wpp-typography-3xl-heading-font-style, normal)}.typography-2xl-heading{font-size:var(--wpp-typography-2xl-heading-font-size, 24px);font-family:var(--wpp-typography-2xl-heading-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-2xl-heading-line-height, 32px);letter-spacing:var(--wpp-typography-2xl-heading-letter-spacing, 0);text-transform:var(--wpp-typography-2xl-heading-text-transform, initial);color:var(--wpp-typography-2xl-heading-color, var(--wpp-text-color));font-weight:var(--wpp-typography-2xl-heading-font-weight, 600);font-style:var(--wpp-typography-2xl-heading-font-style, normal)}.typography-xl-heading{font-size:var(--wpp-typography-xl-heading-font-size, 20px);font-family:var(--wpp-typography-xl-heading-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-xl-heading-line-height, 32px);letter-spacing:var(--wpp-typography-xl-heading-letter-spacing, 0);text-transform:var(--wpp-typography-xl-heading-text-transform, initial);color:var(--wpp-typography-xl-heading-color, var(--wpp-text-color));font-weight:var(--wpp-typography-xl-heading-font-weight, 600);font-style:var(--wpp-typography-xl-heading-font-style, normal)}.typography-l-strong{font-size:var(--wpp-typography-l-body-font-size, 18px);font-family:var(--wpp-typography-l-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-l-body-line-height, 28px);letter-spacing:var(--wpp-typography-l-body-letter-spacing, 0);text-transform:var(--wpp-typography-l-body-text-transform, initial);color:var(--wpp-typography-l-strong-color, var(--wpp-text-color));font-weight:var(--wpp-typography-l-strong-font-weight, 600);font-style:var(--wpp-typography-l-strong-font-style, normal)}.typography-l-midi{font-size:var(--wpp-typography-l-body-font-size, 18px);font-family:var(--wpp-typography-l-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-l-body-line-height, 28px);letter-spacing:var(--wpp-typography-l-body-letter-spacing, 0);text-transform:var(--wpp-typography-l-body-text-transform, initial);color:var(--wpp-typography-l-midi-color, var(--wpp-text-color));font-weight:var(--wpp-typography-l-midi-font-weight, 500);font-style:var(--wpp-typography-l-midi-font-style, normal)}.typography-l-body{font-size:var(--wpp-typography-l-body-font-size, 18px);font-family:var(--wpp-typography-l-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-l-body-line-height, 28px);letter-spacing:var(--wpp-typography-l-body-letter-spacing, 0);text-transform:var(--wpp-typography-l-body-text-transform, initial);color:var(--wpp-typography-l-body-color, var(--wpp-text-color));font-weight:var(--wpp-typography-l-body-font-weight, 400);font-style:var(--wpp-typography-l-body-font-style, normal)}.typography-m-strong{font-size:var(--wpp-typography-m-body-font-size, 16px);font-family:var(--wpp-typography-m-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-m-body-line-height, 24px);letter-spacing:var(--wpp-typography-m-body-letter-spacing, 0);text-transform:var(--wpp-typography-m-body-text-transform, initial);color:var(--wpp-typography-m-strong-color, var(--wpp-text-color));font-weight:var(--wpp-typography-m-strong-font-weight, 600);font-style:var(--wpp-typography-m-strong-font-style, normal)}.typography-m-midi{font-size:var(--wpp-typography-m-body-font-size, 16px);font-family:var(--wpp-typography-m-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-m-body-line-height, 24px);letter-spacing:var(--wpp-typography-m-body-letter-spacing, 0);text-transform:var(--wpp-typography-m-body-text-transform, initial);color:var(--wpp-typography-m-midi-color, var(--wpp-text-color));font-weight:var(--wpp-typography-m-midi-font-weight, 500);font-style:var(--wpp-typography-m-midi-font-style, normal)}.typography-m-body{font-size:var(--wpp-typography-m-body-font-size, 16px);font-family:var(--wpp-typography-m-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-m-body-line-height, 24px);letter-spacing:var(--wpp-typography-m-body-letter-spacing, 0);text-transform:var(--wpp-typography-m-body-text-transform, initial);color:var(--wpp-typography-m-body-color, var(--wpp-text-color));font-weight:var(--wpp-typography-m-body-font-weight, 400);font-style:var(--wpp-typography-m-body-font-style, normal)}.typography-s-strong{font-size:var(--wpp-typography-s-body-font-size, 14px);font-family:var(--wpp-typography-s-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-s-body-line-height, 22px);letter-spacing:var(--wpp-typography-s-body-letter-spacing, 0);text-transform:var(--wpp-typography-s-body-text-transform, initial);color:var(--wpp-typography-s-strong-color, var(--wpp-text-color));font-weight:var(--wpp-typography-s-strong-font-weight, 600);font-style:var(--wpp-typography-s-strong-font-style, normal)}.typography-s-midi{font-size:var(--wpp-typography-s-body-font-size, 14px);font-family:var(--wpp-typography-s-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-s-body-line-height, 22px);letter-spacing:var(--wpp-typography-s-body-letter-spacing, 0);text-transform:var(--wpp-typography-s-body-text-transform, initial);color:var(--wpp-typography-s-midi-color, var(--wpp-text-color));font-weight:var(--wpp-typography-s-midi-font-weight, 500);font-style:var(--wpp-typography-s-midi-font-style, normal)}.typography-s-body{font-size:var(--wpp-typography-s-body-font-size, 14px);font-family:var(--wpp-typography-s-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-s-body-line-height, 22px);letter-spacing:var(--wpp-typography-s-body-letter-spacing, 0);text-transform:var(--wpp-typography-s-body-text-transform, initial);color:var(--wpp-typography-s-body-color, var(--wpp-text-color));font-weight:var(--wpp-typography-s-body-font-weight, 400);font-style:var(--wpp-typography-s-body-font-style, normal)}.typography-xs-strong{font-size:var(--wpp-typography-xs-body-font-size, 12px);font-family:var(--wpp-typography-xs-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-xs-body-line-height, 20px);letter-spacing:var(--wpp-typography-xs-body-letter-spacing, 0);text-transform:var(--wpp-typography-xs-body-text-transform, initial);color:var(--wpp-typography-xs-strong-color, var(--wpp-text-color));font-weight:var(--wpp-typography-xs-strong-font-weight, 600);font-style:var(--wpp-typography-xs-strong-font-style, normal)}.typography-xs-midi{font-size:var(--wpp-typography-xs-body-font-size, 12px);font-family:var(--wpp-typography-xs-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-xs-body-line-height, 20px);letter-spacing:var(--wpp-typography-xs-body-letter-spacing, 0);text-transform:var(--wpp-typography-xs-body-text-transform, initial);color:var(--wpp-typography-xs-midi-color, var(--wpp-text-color));font-weight:var(--wpp-typography-xs-midi-font-weight, 500);font-style:var(--wpp-typography-xs-midi-font-style, normal)}.typography-xs-body{font-size:var(--wpp-typography-xs-body-font-size, 12px);font-family:var(--wpp-typography-xs-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-xs-body-line-height, 20px);letter-spacing:var(--wpp-typography-xs-body-letter-spacing, 0);text-transform:var(--wpp-typography-xs-body-text-transform, initial);color:var(--wpp-typography-xs-body-color, var(--wpp-text-color));font-weight:var(--wpp-typography-xs-body-font-weight, 400);font-style:var(--wpp-typography-xs-body-font-style, normal)}.typography-2xs-strong{font-size:var(--wpp-typography-2xs-strong-font-size, 10px);font-family:var(--wpp-typography-2xs-strong-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-2xs-strong-line-height, 20px);letter-spacing:var(--wpp-typography-2xs-strong-letter-spacing, 0.5px);text-transform:var(--wpp-typography-2xs-strong-text-transform, uppercase);color:var(--wpp-typography-2xs-strong-color, var(--wpp-text-color));font-weight:var(--wpp-typography-2xs-strong-font-weight, 700);font-style:var(--wpp-typography-2xs-strong-font-style, normal)}:host{--autocomplete-border-radius:var(--wpp-autocomplete-border-radius, var(--wpp-border-radius-m));--autocomplete-placeholder-color:var(--wpp-autocomplete-placeholder-color, var(--wpp-grey-color-500));--autocomplete-placeholder-color-disabled:var(--wpp-autocomplete-placeholder-color-disabled, var(--wpp-text-color-disabled));--autocomplete-hidden-count-text-color-disabled:var(--wpp-autocomplete-hidden-count-text-color-disabled, var(--wpp-text-color-disabled));--autocomplete-trigger-icon-right-position:var(--wpp-autocomplete-trigger-actions-right-position, 10px);--autocomplete-inline-message-margin:var(--wpp-autocomplete-inline-message-margin, 4px 0 0 0);--autocomplete-label-margin:var(--wpp-autocomplete-label-margin, 0 0 8px 0);--autocomplete-limit-lines:var(--wpp-autocomplete-limit-lines, 10000);--autocomplete-line-height:var(--wpp-autocomplete-line-height, 34px);--autocomplete-box-shadow:var(--wpp-autocomplete-box-shadow, var(--wpp-box-shadow-m));--autocomplete-first-border-color-focus:var(--wpp-autocomplete-first-border-color-focus, var(--wpp-grey-color-000));--autocomplete-second-border-color-focus:var(--wpp-autocomplete-second-border-color-focus, var(--wpp-brand-color));--autocomplete-height-m:var(--wpp-autocomplete-height-m, 40px);--autocomplete-height-s:var(--wpp-autocomplete-height-s, 32px);--autocomplete-padding-s:var(--wpp-autocomplete-padding-s, 5px 0 5px 12px);--autocomplete-padding-m:var(--wpp-autocomplete-padding-m, 9px 0 9px 12px);--autocomplete-bg-color:var(--wpp-autocomplete-bg-color, transparent);--autocomplete-bg-color-hover:var(--wpp-autocomplete-bg-color-hover, var(--wpp-grey-color-200));--autocomplete-bg-color-active:var(--wpp-autocomplete-bg-color-active, transparent);--autocomplete-bg-color-focused:var(--wpp-autocomplete-bg-color-focused, var(--wpp-grey-color-000));--autocomplete-bg-color-disabled:var(--wpp-autocomplete-bg-color-disabled, var(--wpp-grey-color-100));--autocomplete-trigger-icon-color:var(--wpp-autocomplete-trigger-actions-right-position, var(--wpp-icon-color));--autocomplete-trigger-icon-color-hover:var(--wpp-autocomplete-trigger-icon-color-hover, var(--wpp-icon-color-hover));--autocomplete-trigger-icon-color-active:var(--wpp-autocomplete-trigger-icon-color-active, var(--wpp-icon-color-active));--autocomplete-trigger-icon-color-disabled:var(--wpp-autocomplete-trigger-icon-color-disabled, var(--wpp-icon-color-disabled));--autocomplete-border-color:var(--wpp-autocomplete-border-color, var(--wpp-grey-color-500));--autocomplete-border-color-hover:var(--wpp-autocomplete-border-color-hover, var(--wpp-grey-color-700));--autocomplete-border-color-active:var(--wpp-autocomplete-border-color-active, var(--wpp-grey-color-800));--autocomplete-border-color-focused:var(--wpp-autocomplete-border-color-focused, var(--wpp-grey-color-800));--autocomplete-border-color-disabled:var(--wpp-autocomplete-border-color-disabled, var(--wpp-grey-color-400));--autocomplete-border-width:var(--wpp-autocomplete-border-width, var(--wpp-border-width-s));--autocomplete-border-style:var(--wpp-autocomplete-border-style, solid);--autocomplete-single-border-color-disabled:var(--wpp-autocomplete-single-border-color-disabled, var(--wpp-grey-color-400));--autocomplete-dropdown-max-height:var(--wpp-autocomplete-dropdown-max-height, 372px);--autocomplete-dropdown-checkbox-margin:var(--wpp-autocomplete-dropdown-checkbox-margin, 1px 8px 1px 0);--autocomplete-dropdown-bg-color:var(--wpp-autocomplete-dropdown-bg-color, var(--wpp-grey-color-000));--autocomplete-dropdown-border-radius:var(--wpp-autocomplete-dropdown-border-radius, var(--wpp-border-radius-s));--autocomplete-dropdown-padding:var(--wpp-autocomplete-dropdown-padding, 8px);--autocomplete-create-new-element-color:var(--wpp-autocomplete-create-new-element-color, var(--wpp-primary-color-500));--autocomplete-nothing-found-message-color:var(--wpp-autocomplete-nothing-found-message-color, var(--wpp-grey-color-700));--autocomplete-search-icon-margin-right:var(--wpp-autocomplete-search-icon-margin-right, 8px);--autocomplete-item-margin-bottom:var(--wpp-autocomplete-item-margin-bottom, 4px);--autocomplete-regular-selected-values-wrapper-padding:var(--wpp-autocomplete-regular-selected-values-wrapper-padding, 10px 10px 2px 10px);--autocomplete-extended-selected-values-wrapper-padding:var(--wpp-autocomplete-extended-selected-values-wrapper-padding, 10px 10px 2px 10px);position:relative;display:block;outline:none}:host ::slotted([slot=selected-values]){margin-top:8px;display:-ms-flexbox;display:flex;-ms-flex-wrap:wrap;flex-wrap:wrap}:host .label:not(.focused):hover+.trigger{background-color:var(--autocomplete-bg-color-hover);border-color:var(--autocomplete-border-color-hover)}:host .label:not(.focused):hover+.trigger .trigger-actions .wpp-icon-chevron,:host .label:not(.focused):hover+.trigger .wpp-icon-cross{color:var(--autocomplete-trigger-icon-color-hover)}:host .label:not(.focused):hover+.trigger.warning{border-color:var(--wpp-warning-color-500)}:host .label:not(.focused):hover+.trigger.error{border-color:var(--wpp-danger-color-500)}:host(.disabled){cursor:not-allowed}:host(.disabled) .label{pointer-events:none}.trigger-actions{position:absolute;top:50%;-webkit-transform:translateY(-50%);transform:translateY(-50%);right:var(--autocomplete-trigger-icon-right-position);display:-ms-flexbox;display:flex}.trigger-actions .wpp-icon-chevron,.trigger-actions .wpp-icon-cross{color:var(--autocomplete-trigger-icon-color);cursor:pointer;-webkit-transition:all 0.15s ease-out 0s;transition:all 0.15s ease-out 0s}.values{scrollbar-width:thin;scrollbar-color:var(--wpp-grey-color-400) transparent;display:-ms-flexbox;display:flex;-ms-flex:1 1 auto;flex:1 1 auto;-ms-flex-align:center;align-items:center;min-width:0;max-height:calc(var(--autocomplete-limit-lines) * var(--autocomplete-line-height));padding-right:36px;overflow-y:auto}.values::-webkit-scrollbar{width:8px;height:8px}.values::-webkit-scrollbar-thumb{border:2px solid transparent;border-radius:4px;-webkit-box-shadow:inset 0 0 0 2px var(--wpp-grey-color-400);box-shadow:inset 0 0 0 2px var(--wpp-grey-color-400)}.values .hidden-count{-ms-flex:0 1 auto;flex:0 1 auto;margin:3px 2px;min-width:calc(25px + (var(--hidden-number) - 1) * 7.5px)}.values .hidden-count{font-size:var(--wpp-typography-s-body-font-size, 14px);font-family:var(--wpp-typography-s-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-s-body-line-height, 22px);letter-spacing:var(--wpp-typography-s-body-letter-spacing, 0);text-transform:var(--wpp-typography-s-body-text-transform, initial);color:var(--wpp-typography-s-strong-color, var(--wpp-text-color));font-weight:var(--wpp-typography-s-strong-font-weight, 600);font-style:var(--wpp-typography-s-strong-font-style, normal)}.values .autocomplete-input{font-size:var(--wpp-typography-s-body-font-size, 14px);font-family:var(--wpp-typography-s-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-s-body-line-height, 22px);letter-spacing:var(--wpp-typography-s-body-letter-spacing, 0);text-transform:var(--wpp-typography-s-body-text-transform, initial);color:var(--wpp-typography-s-body-color, var(--wpp-text-color));font-weight:var(--wpp-typography-s-body-font-weight, 400);font-style:var(--wpp-typography-s-body-font-style, normal);-ms-flex:1 1 70px;flex:1 1 70px;min-width:70px;padding:0;background:var(--autocomplete-bg-color);border:none;outline:none;overflow:hidden;white-space:nowrap;text-overflow:ellipsis}.values .autocomplete-input.hidden{position:absolute;right:0;bottom:0;z-index:-1;width:1px;min-width:1px;height:1px;opacity:0}.values .autocomplete-input::-webkit-input-placeholder{color:var(--autocomplete-placeholder-color)}.values .autocomplete-input::-moz-placeholder{color:var(--autocomplete-placeholder-color)}.values .autocomplete-input:-ms-input-placeholder{color:var(--autocomplete-placeholder-color)}.values .autocomplete-input::-ms-input-placeholder{color:var(--autocomplete-placeholder-color)}.values .autocomplete-input::placeholder{color:var(--autocomplete-placeholder-color)}.values .input-placeholder{overflow:hidden;white-space:nowrap;text-overflow:ellipsis;width:100%}.values .wpp-chip+.autocomplete-input{margin-left:6px}.count-block{display:none}.trigger{position:relative;display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;-webkit-box-sizing:border-box;box-sizing:border-box;background-color:transparent;border:var(--autocomplete-border-width) var(--autocomplete-border-style) var(--autocomplete-border-color);border-radius:var(--autocomplete-border-radius);cursor:text}.trigger.size-s{height:var(--autocomplete-height-s);padding:var(--autocomplete-padding-s)}.trigger.size-m{height:var(--autocomplete-height-m);padding:var(--autocomplete-padding-m)}.trigger:hover{background-color:var(--autocomplete-bg-color-hover);border-color:var(--autocomplete-border-color-hover)}.trigger:hover .trigger-actions .wpp-icon-chevron,.trigger:hover .wpp-icon-cross{color:var(--autocomplete-trigger-icon-color-hover)}.trigger:active{background-color:var(--autocomplete-bg-color-active);border-color:var(--autocomplete-border-color-active)}.trigger:active .trigger-actions .wpp-icon-chevron,.trigger:active .wpp-icon-cross{color:var(--autocomplete-trigger-icon-color-active)}.trigger.focused{background-color:var(--autocomplete-bg-color-focused);border-color:var(--autocomplete-border-color-focused)}.trigger.focused .values{padding-right:35px}.trigger.warning,.trigger.warning:hover{border:var(--autocomplete-border-width) var(--autocomplete-border-style) var(--wpp-warning-color-400)}.trigger.warning.tab-focus{border-radius:\"\";outline:none;-webkit-box-shadow:0 0 0 1px var(--wpp-grey-color-000), 0 0 0 3px var(--wpp-warning-color-400);box-shadow:0 0 0 1px var(--wpp-grey-color-000), 0 0 0 3px var(--wpp-warning-color-400)}.trigger.error,.trigger.error:hover{border:var(--autocomplete-border-width) var(--autocomplete-border-style) var(--wpp-danger-color-400)}.trigger.error.tab-focus{border-radius:\"\";outline:none;-webkit-box-shadow:0 0 0 1px var(--wpp-grey-color-000), 0 0 0 3px var(--wpp-danger-color-400);box-shadow:0 0 0 1px var(--wpp-grey-color-000), 0 0 0 3px var(--wpp-danger-color-400)}.trigger.tab-focus{border-radius:\"\";outline:none;-webkit-box-shadow:0 0 0 1px var(--autocomplete-first-border-color-focus), 0 0 0 3px var(--autocomplete-second-border-color-focus);box-shadow:0 0 0 1px var(--autocomplete-first-border-color-focus), 0 0 0 3px var(--autocomplete-second-border-color-focus)}.trigger.disabled{background-color:var(--autocomplete-bg-color-disabled);border-color:var(--autocomplete-border-color-disabled);pointer-events:none}.trigger.disabled .values .autocomplete-input{color:var(--wpp-grey-color-500)}.trigger.disabled .values .autocomplete-input::-webkit-input-placeholder{color:var(--autocomplete-placeholder-color-disabled)}.trigger.disabled .values .autocomplete-input::-moz-placeholder{color:var(--autocomplete-placeholder-color-disabled)}.trigger.disabled .values .autocomplete-input:-ms-input-placeholder{color:var(--autocomplete-placeholder-color-disabled)}.trigger.disabled .values .autocomplete-input::-ms-input-placeholder{color:var(--autocomplete-placeholder-color-disabled)}.trigger.disabled .values .autocomplete-input::placeholder{color:var(--autocomplete-placeholder-color-disabled)}.trigger.disabled .values .hidden-count .wpp-typography{color:var(--autocomplete-hidden-count-text-color-disabled)}.trigger.disabled .values .input-placeholder{color:var(--autocomplete-placeholder-color-disabled)}.trigger.disabled .trigger-actions .wpp-icon-chevron,.trigger.disabled .wpp-icon-cross,.trigger.disabled .wpp-icon-search{color:var(--autocomplete-trigger-icon-color-disabled)}.trigger.disabled{border-color:var(--autocomplete-single-border-color-disabled)}.trigger[aria-expanded=true]>.trigger-actions .wpp-icon-chevron{-webkit-transform:rotate(180deg);transform:rotate(180deg)}.trigger .wpp-icon-search{margin-right:var(--autocomplete-search-icon-margin-right)}.wpp-inline-message::part(message-block){margin:var(--autocomplete-inline-message-margin)}.dropdown{display:-ms-flexbox;display:flex;-ms-flex-direction:column;flex-direction:column;-webkit-box-sizing:border-box;box-sizing:border-box;max-height:var(--autocomplete-dropdown-max-height);overflow:hidden;background:var(--autocomplete-dropdown-bg-color);border-radius:var(--autocomplete-dropdown-border-radius);-webkit-box-shadow:var(--autocomplete-box-shadow);box-shadow:var(--autocomplete-box-shadow);width:var(--custom-dropdown-width)}.dropdown .dropdown-list{scrollbar-width:thin;scrollbar-color:var(--wpp-grey-color-400) transparent;min-height:0;padding:var(--autocomplete-dropdown-padding);overflow-x:hidden;overflow-y:auto}.dropdown .dropdown-list::-webkit-scrollbar{width:8px;height:8px}.dropdown .dropdown-list::-webkit-scrollbar-thumb{border:2px solid transparent;border-radius:4px;-webkit-box-shadow:inset 0 0 0 2px var(--wpp-grey-color-400);box-shadow:inset 0 0 0 2px var(--wpp-grey-color-400)}.dropdown .dropdown-list.hidden{display:none}.dropdown .dropdown-list:empty{display:none}.dropdown .dropdown-list .infinite-loader{display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;-ms-flex-pack:center;justify-content:center;height:32px}.dropdown .dropdown-list .loading{pointer-events:none}.dropdown .dropdown-list .loading ::part(info-wrapper){height:inherit}.dropdown .dropdown-list .loading .wpp-spinner{margin-right:7px}.dropdown .dropdown-list .nothing-found-wrapper{pointer-events:none}.dropdown .dropdown-list .nothing-found-wrapper .nothing-found{color:var(--autocomplete-nothing-found-message-color)}.dropdown .dropdown-list .nothing-found-divider{margin:8px 0}.dropdown .dropdown-list .create-new-option{color:var(--autocomplete-create-new-element-color)}.dropdown .dropdown-list .selected-values{display:-ms-flexbox;display:flex;-webkit-transition-delay:300ms;transition-delay:300ms;-webkit-transition-property:padding-top;transition-property:padding-top}.dropdown .dropdown-list .selected-values.focused{-webkit-transition-delay:0s;transition-delay:0s;-webkit-transition-property:padding-top;transition-property:padding-top}.dropdown .dropdown-list .selected-values.regular{padding:var(--autocomplete-regular-selected-values-wrapper-padding)}.dropdown .dropdown-list .selected-values.regular.focused{--autocomplete-regular-selected-values-wrapper-padding:2px 10px 2px 10px}.dropdown .dropdown-list .selected-values.extended{padding:var(--autocomplete-extended-selected-values-wrapper-padding)}.dropdown .dropdown-list .selected-values.extended.focused{--autocomplete-extended-selected-values-wrapper-padding:2px 10px 2px 10px}.dropdown .dropdown-list ::slotted(.wpp-list-item){width:100%}.dropdown .dropdown-list ::slotted(.wpp-list-item:not(:last-child)){margin-bottom:var(--autocomplete-item-margin-bottom)}.label{margin:var(--autocomplete-label-margin)}";

// Load more will be triggered 15px before scroll ends
const INFINITE_SCROLL_THRESHOLD = 15;
const WppAutocomplete$1 = /*@__PURE__*/ proxyCustomElement(class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.wppChange = createEvent(this, "wppChange", 1);
    this.wppFocus = createEvent(this, "wppFocus", 1);
    this.wppBlur = createEvent(this, "wppBlur", 1);
    this.wppSearchValueChange = createEvent(this, "wppSearchValueChange", 1);
    this.wppCreateNewOption = createEvent(this, "wppCreateNewOption", 1);
    this.isScrollToInputRequested = false;
    this.hasChecked = false;
    // Used instead of Tippy's `state.isShown`, which is not updated when transitioning
    this.isDropdownShown = false;
    this.valueResizeObserver = () => {
      if (this.valuesContainerEl) {
        this.valuesResizeObserver = new ResizeObserver(() => {
          if (this.type === 'regular') {
            this.countHiddenElements();
          }
          if (this.isDropdownShown) {
            this.tippyInstance?.popperInstance?.forceUpdate();
          }
        });
        this.valuesResizeObserver.observe(this.valuesContainerEl);
      }
    };
    this.createTippyInstance = () => {
      if (this.triggerEl && this.dropdownEl) {
        this.tippyInstance = menuListConfig({
          anchor: this.triggerEl,
          content: this.dropdownEl,
          zIndex: Z_INDEX.CONTEXT_MENU,
          ...this.dropdownConfig,
          duration: DROPDOWN_ANIMATION_TIME,
          triggerElementWidth: true,
          trigger: 'manual',
          maxWidth: 'none',
          hideOnClick: false,
          popperOptions: {
            ...this.dropdownConfig?.popperOptions,
            modifiers: [
              ...(this.dropdownConfig?.popperOptions?.modifiers || []),
              {
                name: 'flip',
                options: {
                  fallbackPlacements: ['top'],
                },
              },
            ],
          },
          onClickOutside: (_, event) => {
            if (!isEventTargetContained(this.host, event)) {
              this.hideDropdown();
            }
          },
        });
      }
    };
    this.isSelectedItemsLimitReached = () => {
      if (this.limitSelectedItems <= 0)
        return false;
      return this.value.length >= this.limitSelectedItems;
    };
    this.canLoadMore = () => this.infinite && !this.infiniteLastPage && this.loadMore && !this.isInfiniteLoading;
    this.hasClearButton = () => !!this.value.length && !this.isDropdownShown;
    this.isSelectedOptionsDisplayed = () => !this.searchValue;
    this.hasSearchButton = () => true;
    this.hasSimpleSearch = () => this.simpleSearch && !this.infinite;
    this.isOptionHidden = (option) => {
      if (!this.hasSimpleSearch()) {
        return false;
      }
      const trimmedSearch = this.searchValue.trim().toLocaleLowerCase();
      if (trimmedSearch.length > 0) {
        const optionValue = option.value;
        if (!optionValue) {
          return false;
        }
        const optionLabel = (this.getOptionLabel(optionValue) || '').toLocaleLowerCase();
        return !optionLabel.includes(trimmedSearch);
      }
      return false;
    };
    this.isOptionChecked = (option) => {
      if (option.value) {
        const checkedID = this.getOptionId(option.value);
        const isSelected = this.value.some(selectedValue => this.getOptionId(selectedValue) === checkedID);
        if (!this.multiple && this.value.length > 1) {
          return this.getOptionId(this.value[0]) === checkedID;
        }
        return isSelected;
      }
      return false;
    };
    this.isOptionNodesChanged = (nextOptions) => nextOptions.length !== this.optionElements?.length ||
      !nextOptions.every((el, index) => this.optionElements?.[index] === el);
    this.getOptionElements = () => Array.from(this.host.querySelectorAll(transformToVersionedTag('wpp-list-item')));
    this.scrollOptionsToTop = () => {
      if (this.optionsListEl) {
        this.optionsListEl.scrollTop = 0;
      }
    };
    this.focusInput = () => {
      this.inputEl?.focus();
    };
    this.blurInput = () => {
      this.inputEl?.blur();
    };
    this.showDropdown = () => {
      if (!this.isDropdownShown) {
        this.isDropdownShown = true;
        this.tippyInstance?.show();
      }
    };
    this.hideDropdown = () => {
      if (this.isDropdownShown) {
        if (this.type === 'regular') {
          this.countHiddenElements();
        }
        this.tippyInstance?.hide();
        this.isDropdownShown = false;
      }
    };
    this.updateOptions = () => {
      this.shownOptionElements = [];
      this.optionElements?.forEach(option => {
        option.selectable = true;
        option.hidden = this.isOptionHidden(option);
        option.checked = this.isOptionChecked(option);
        option.highlight = this.searchValue;
        if (!option.hidden) {
          this.shownOptionElements.push(option);
        }
      });
      this.isEmptyOptions = !this.shownOptionElements.length;
    };
    this.requestLoadMore = () => {
      if (this.loadMore) {
        this.isInfiniteLoading = true;
        const promise = this.loadMore().finally(() => {
          if (!promise.cancelled) {
            this.isInfiniteLoading = false;
            this.infiniteLoadingPromise = undefined;
          }
        });
        this.infiniteLoadingPromise = promise;
      }
    };
    this.handleTriggerContainerMouseDown = (event) => {
      if (!this.disabled) {
        // Prevent input blur when the component is used
        if (event.target !== this.inputEl) {
          event.preventDefault();
        }
      }
    };
    this.handleCreateNewOptionClick = () => {
      this.wppCreateNewOption.emit(this.searchValue);
    };
    this.handleTriggerClick = () => {
      if (!this.isFocused) {
        this.focusInput();
      }
    };
    this.handleMouseDown = () => {
      this.focusType = FOCUS_TYPE.MOUSE;
    };
    this.handleKeyUp = (event) => {
      if (event.key === 'Tab')
        this.focusType = FOCUS_TYPE.TAB;
    };
    this.handleInput = () => {
      this.focusType = FOCUS_TYPE.NONE;
      this.searchValue = this.inputEl?.value || '';
      if (this.searchValue) {
        this.showDropdown();
      }
      else if (!this.value.length) {
        this.hideDropdown();
      }
    };
    this.handleFocus = (event) => {
      if (!this.isFocused) {
        this.isScrollToInputRequested = true;
        this.isFocused = true;
        if (this.value.length) {
          this.showDropdown();
        }
        if (this.canLoadMore() && this.isEmptyOptions && !this.loading) {
          this.requestLoadMore();
        }
      }
      this.wppFocus.emit(event);
    };
    // We allow input blur only when the dropdown is hidden or the component got disabled.
    // Outside clicks will close the dropdown first.
    this.handleBlur = (event) => {
      this.focusType = FOCUS_TYPE.NONE;
      if (!event.relatedTarget)
        this.hideDropdown();
      setTimeout(() => {
        if (!this.isDropdownShown) {
          this.isFocused = false;
          // Need setTimeout method to wait until closing animation is finished
          setTimeout(() => {
            this.searchValue = '';
          }, BLUR_TIME);
        }
        else {
          this.focusInput();
        }
      }, 0);
      this.wppBlur.emit(event);
    };
    this.handleOptionsScroll = (event) => {
      if (this.canLoadMore()) {
        const container = event.target;
        const scrolledToBottom = container.scrollHeight - container.clientHeight - container.scrollTop;
        if (scrolledToBottom < INFINITE_SCROLL_THRESHOLD) {
          this.requestLoadMore();
        }
      }
    };
    // For some reason this handler is not triggered by the browser
    // while Tippy instance is being created. Though it is covered
    // after the dropdown is opened, since Tippy moves the dropdown node.
    this.handleOptionsChange = (shouldUpdate = false) => {
      const currentNodes = this.getOptionElements();
      const isNodesChanged = this.isOptionNodesChanged(currentNodes);
      if (isNodesChanged || shouldUpdate) {
        this.optionElements = currentNodes;
        this.updateOptions();
      }
    };
    this.handleClearClick = (event) => {
      event.stopPropagation();
      this.value = [];
      this.hiddenSelectedOptionsNumber = 0;
      this.wppChange.emit({ value: this.value, reason: 'removeOption' });
    };
    this.hostCssClasses = () => ({
      'wpp-autocomplete': true,
      disabled: this.disabled,
    });
    this.autocompleteWrapperCssClasses = () => ({
      'autocomplete-wrapper': true,
    });
    this.triggerCssClasses = () => ({
      trigger: true,
      disabled: this.disabled,
      focused: this.isFocused,
      [`${this.messageType}`]: !!this.messageType,
      [`size-${this.size}`]: !!this.size,
      'tab-focus': this.focusType === FOCUS_TYPE.TAB,
    });
    this.inputCssClasses = () => ({
      'autocomplete-input': true,
      hidden: !this.isFocused && this.value.length > 0,
    });
    this.labelCssClasses = () => ({
      label: true,
      focused: this.isFocused,
    });
    this.dropdownListCssClasses = () => ({
      'dropdown-list': true,
      hidden: this.searchValue.length !== this.searchValue.trim().length && !this.searchValue.trim().length,
    });
    this.selectedValuesCssClasses = () => ({
      'selected-values': true,
      focused: this.isFocused,
      [`${this.type}`]: true,
    });
    this.hostStyle = () => {
      const style = {
        '--wpp-list-item-width': '100%',
      };
      return style;
    };
    this.getInputValue = () => this.searchValue;
    this.renderInputPlaceholder = () => {
      if (!this.multiple && this.value.length && !this.isFocused) {
        return (h("wpp-typography-v2-9-0", { type: "s-body", class: "input-placeholder" }, this.getOptionLabel(this.value[0])));
      }
      if (this.isFocused && !this.searchValue)
        return null;
      if (this.isDropdownShown)
        return null;
      if (this.type === 'regular') {
        const itemsToDisplay = this.value;
        if (!itemsToDisplay.length)
          return null;
        const placeholder = itemsToDisplay.map(this.getOptionLabel).filter(Boolean).join(', ');
        return (h("wpp-typography-v2-9-0", { type: "s-body", class: "input-placeholder" }, placeholder));
      }
      if (this.type === 'extended' && this.value.length && !this.isFocused) {
        return (h("wpp-typography-v2-9-0", { type: "s-body", class: "input-placeholder" }, this.locales.selected(this.value.length)));
      }
    };
    this.countHiddenElements = () => {
      const el = this.host.shadowRoot.querySelector('.input-placeholder');
      if (!el) {
        this.hiddenSelectedOptionsNumber = 0;
        return 0;
      }
      const shownItems = this.value;
      const maxWidth = el.clientWidth;
      const textStyles = getComputedStyle(el, null).cssText;
      let displayedElements = 1;
      let label = this.getOptionLabel(shownItems[0]);
      for (let i = 1; i < shownItems.length; i++) {
        label += this.getOptionLabel(shownItems[i]);
        const currWidth = getTempNodeWidthBasedOnLabel(textStyles, label);
        if (currWidth > maxWidth) {
          this.hiddenSelectedOptionsNumber = this.value.length - displayedElements;
          return;
        }
        else {
          displayedElements++;
        }
      }
      this.hiddenSelectedOptionsNumber = this.value.length - displayedElements;
    };
    this.getNearestPowForRowsNumber = () => Math.ceil(Math.log10(this.hiddenSelectedOptionsNumber + 1));
    this.renderDropdownContent = () => {
      const isLoading = this.loading || (this.isInfiniteLoading && this.isEmptyOptions);
      const isEmptyStringEntered = this.searchValue.trim().length === 0;
      if (isEmptyStringEntered && !this.value.length) {
        return null;
      }
      if (isLoading) {
        return (h("wpp-list-item-v2-9-0", { isLoadingItem: true, class: "loading" }, h("wpp-spinner-v2-9-0", { slot: "left" }), h("wpp-typography-v2-9-0", { type: "s-body", slot: "label" }, this.locales.loading)));
      }
      if (this.isEmptyOptions) {
        return (h(Fragment, null, h("wpp-list-item-v2-9-0", { class: "nothing-found-wrapper" }, h("wpp-typography-v2-9-0", { type: "s-body", class: "nothing-found", slot: "label" }, this.locales.nothingFound)), this.showCreateNewElement && (h(Fragment, null, h("wpp-divider-v2-9-0", { class: "nothing-found-divider" }), h("wpp-list-item-v2-9-0", { onClick: this.handleCreateNewOptionClick }, h("wpp-typography-v2-9-0", { type: "s-strong", class: "create-new-option", slot: "label" }, this.locales.createNewElement))))));
      }
      if (!this.multiple && this.value.length && isEmptyStringEntered) {
        return null;
      }
      return (h(Fragment, null, this.isSelectedOptionsDisplayed() ? (h("div", { class: this.selectedValuesCssClasses(), part: "selected-values" }, h("slot", { name: "selected-values" }))) : (h("slot", { onSlotchange: () => this.handleOptionsChange() })), h("div", null, this.isInfiniteLoading && (h("div", { class: "infinite-loader" }, h("wpp-spinner-v2-9-0", null))))));
    };
    this.isFocused = false;
    this.searchValue = '';
    this.isEmptyOptions = true;
    this.isInfiniteLoading = false;
    this.focusType = undefined;
    this.hiddenSelectedOptionsNumber = 0;
    this.name = undefined;
    this.loading = false;
    this.disabled = false;
    this.autoFocus = false;
    this.infinite = false;
    this.infiniteLastPage = true;
    this.limitSelectedItems = 0;
    this.placeholder = undefined;
    this.value = [];
    this.getOptionId = item => item.id;
    this.getOptionLabel = item => item.label;
    this.loadMore = undefined;
    this.required = false;
    this.message = undefined;
    this.messageType = undefined;
    this.maxMessageLength = undefined;
    this.dropdownConfig = {};
    this.type = 'regular';
    this.size = 'm';
    this.locales = {
      nothingFound: 'Nothing found',
      more: 'more',
      selected: count => `${count} selected`,
      loading: 'Loading...',
      createNewElement: 'Create new element',
    };
    this.labelTooltipConfig = {
      popperOptions: { strategy: 'fixed' },
    };
    this.labelConfig = undefined;
    this.multiple = false;
    this.showCreateNewElement = false;
    this.simpleSearch = false;
    this.dropdownWidth = 'auto';
  }
  handleOptionToggle(event) {
    const isCurrValueAlreadySelected = this.value.find(option => this.getOptionId(option) === this.getOptionId(event.detail.value));
    if (this.isSelectedItemsLimitReached() && !isCurrValueAlreadySelected) {
      const listItem = event.target;
      listItem.checked = false;
      return;
    }
    this.value = event.detail.checked
      ? [event.detail.value, ...(this.multiple ? this.value : [])]
      : this.value.filter(option => this.getOptionId(option) !== this.getOptionId(event.detail.value));
    this.searchValue = '';
    this.wppChange.emit({ value: this.value, reason: event.detail.checked ? 'selectOption' : 'removeOption' });
  }
  onLoadingChange(loading) {
    setTimeout(() => {
      this.handleOptionsChange();
    }, 0);
    if (loading) {
      this.scrollOptionsToTop();
      if (this.isInfiniteLoading) {
        this.isInfiniteLoading = false;
        if (this.infiniteLoadingPromise) {
          this.infiniteLoadingPromise.cancelled = true;
        }
      }
    }
  }
  onNextValueChange(newValue) {
    if (!newValue.length && this.isDropdownShown) {
      this.hideDropdown();
    }
    if (this.isSelectedItemsLimitReached()) {
      this.hideDropdown();
      this.blurInput();
      this.searchValue = '';
    }
    if (!this.multiple) {
      this.hideDropdown();
      this.blurInput();
    }
  }
  onSearchValueChange(initSearchValue) {
    const searchValue = initSearchValue.trim();
    if (!this.hasSimpleSearch()) {
      this.wppSearchValueChange.emit(searchValue);
      setTimeout(() => {
        this.handleOptionsChange(true);
      }, 0);
      return;
    }
    if (!searchValue) {
      this.optionElements?.forEach(option => {
        option.hidden = true;
      });
      this.shownOptionElements = [];
      this.isEmptyOptions = false;
      return [];
    }
    this.shownOptionElements = [];
    this.optionElements?.forEach(option => {
      option.hidden = this.isOptionHidden(option);
      option.checked = this.isOptionChecked(option);
      option.highlight = searchValue;
      if (!option.hidden) {
        this.shownOptionElements.push(option);
      }
    });
    this.isEmptyOptions = !this.shownOptionElements.length;
    this.wppSearchValueChange.emit(searchValue);
  }
  updateDropdownConfig(newConfig, oldConfig) {
    if (!isEqual_1(newConfig, oldConfig)) {
      this.dropdownConfig = newConfig;
      this.tippyInstance?.setProps(newConfig);
    }
  }
  /**
   * Sets focus on native input
   */
  async setFocus() {
    this.inputEl?.focus();
  }
  componentWillLoad() {
    if (this.limitSelectedItems > 0 && !this.multiple) {
      throw new Error('There could be only one selected item in single mode, otherwise, use multiple mode.');
    }
    this.optionElements = this.getOptionElements();
    this.updateOptions();
  }
  componentDidLoad() {
    // Watches the size of values container, which changes when
    // autocomplete is focused and `limitLines` prop is set
    this.valueResizeObserver();
    this.createTippyInstance();
    autoFocusElement(this.autoFocus, this.inputEl);
  }
  disconnectedCallback() {
    if (this.valuesResizeObserver) {
      this.valuesResizeObserver.disconnect();
    }
    this.tippyInstance?.destroy();
  }
  connectedCallback() {
    this.valueResizeObserver();
    if (this.tippyInstance?.state.isDestroyed) {
      this.createTippyInstance();
    }
  }
  render() {
    const style = {
      '--custom-dropdown-width': this.dropdownWidth === 'auto' ? '' : this.dropdownWidth,
    };
    return (h(Host, { style: this.hostStyle(), class: this.hostCssClasses(), onFocus: this.handleFocus, onBlur: this.handleBlur, onMouseDown: this.handleMouseDown, onKeyUp: this.handleKeyUp, "aria-disabled": this.disabled, "aria-required": this.required, exportparts: "input, dropdown, options, selected-values" }, h("div", { class: this.autocompleteWrapperCssClasses(), onMouseDown: this.handleTriggerContainerMouseDown }, this.labelConfig?.text && (h("wpp-label-v2-9-0", { class: this.labelCssClasses(), htmlFor: this.name, disabled: this.disabled, optional: !this.required, config: this.labelConfig, tooltipConfig: this.labelTooltipConfig })), h("div", { ref: triggerEl => (this.triggerEl = triggerEl), class: this.triggerCssClasses(), onClick: this.handleTriggerClick }, h("div", { ref: valuesEl => (this.valuesContainerEl = valuesEl), class: "values" }, this.hasSearchButton() && h("wpp-icon-search-v2-9-0", null), this.renderInputPlaceholder(), this.hiddenSelectedOptionsNumber > 0 && !this.isDropdownShown && (h("div", { class: "hidden-count", style: { '--hidden-number': this.getNearestPowForRowsNumber() + '' } }, h("wpp-typography-v2-9-0", { type: "s-body" }, "+ ", this.hiddenSelectedOptionsNumber))), h("input", { part: "input", ref: inputEl => (this.inputEl = inputEl), class: this.inputCssClasses(), id: this.name, name: this.name, type: "text", value: this.getInputValue(), disabled: this.disabled, placeholder: this.placeholder, required: this.required, autocomplete: "off", onInput: this.handleInput, tabIndex: this.disabled ? -1 : 0, title: "" })), h("div", { class: "trigger-actions" }, this.hasClearButton() && h("wpp-icon-cross-v2-9-0", { onClick: this.handleClearClick }))), !!this.message && (h("wpp-inline-message-v2-9-0", { class: "inline-message", showTooltipFrom: this.maxMessageLength, message: this.message, type: this.messageType }))), h("div", { class: "dropdown", part: "dropdown", ref: dropdownEl => (this.dropdownEl = dropdownEl), style: style }, h("div", { ref: optionsListEl => (this.optionsListEl = optionsListEl), part: "options", class: this.dropdownListCssClasses(), onScroll: this.handleOptionsScroll }, this.renderDropdownContent())), this.type === 'extended' && this.multiple ? h("slot", { name: "selected-values" }) : null));
  }
  static get registryIs() { return "wpp-autocomplete-v2-9-0"; }
  get host() { return this; }
  static get watchers() { return {
    "loading": ["onLoadingChange"],
    "value": ["onNextValueChange"],
    "searchValue": ["onSearchValueChange"],
    "dropdownConfig": ["updateDropdownConfig"]
  }; }
  static get style() { return wppAutocompleteCss; }
}, [1, "wpp-autocomplete", "wpp-autocomplete-v2-9-0", {
    "name": [1],
    "loading": [516],
    "disabled": [516],
    "autoFocus": [4, "auto-focus"],
    "infinite": [4],
    "infiniteLastPage": [4, "infinite-last-page"],
    "limitSelectedItems": [2, "limit-selected-items"],
    "placeholder": [1],
    "value": [1040],
    "getOptionId": [16],
    "getOptionLabel": [16],
    "loadMore": [16],
    "required": [516],
    "message": [1],
    "messageType": [1, "message-type"],
    "maxMessageLength": [2, "max-message-length"],
    "dropdownConfig": [1040],
    "type": [1],
    "size": [1],
    "locales": [16],
    "labelTooltipConfig": [16],
    "labelConfig": [1040],
    "multiple": [4],
    "showCreateNewElement": [4, "show-create-new-element"],
    "simpleSearch": [4, "simple-search"],
    "dropdownWidth": [1, "dropdown-width"],
    "isFocused": [32],
    "searchValue": [32],
    "isEmptyOptions": [32],
    "isInfiniteLoading": [32],
    "focusType": [32],
    "hiddenSelectedOptionsNumber": [32],
    "setFocus": [64]
  }, [[2, "wppChangeListItem", "handleOptionToggle"]]]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["wpp-autocomplete-v2-9-0", "wpp-checkbox-v2-9-0", "wpp-divider-v2-9-0", "wpp-icon-chevron-v2-9-0", "wpp-icon-cross-v2-9-0", "wpp-icon-dash-v2-9-0", "wpp-icon-error-v2-9-0", "wpp-icon-info-v2-9-0", "wpp-icon-search-v2-9-0", "wpp-icon-success-v2-9-0", "wpp-icon-tick-v2-9-0", "wpp-icon-warning-v2-9-0", "wpp-inline-message-v2-9-0", "wpp-internal-label-v2-9-0", "wpp-internal-tooltip-v2-9-0", "wpp-label-v2-9-0", "wpp-list-item-v2-9-0", "wpp-spinner-v2-9-0", "wpp-tooltip-v2-9-0", "wpp-typography-v2-9-0"];
  components.forEach(tagName => { switch (tagName) {
    case "wpp-autocomplete-v2-9-0":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, WppAutocomplete$1);
      }
      break;
    case "wpp-checkbox-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$k();
      }
      break;
    case "wpp-divider-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$j();
      }
      break;
    case "wpp-icon-chevron-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$i();
      }
      break;
    case "wpp-icon-cross-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$h();
      }
      break;
    case "wpp-icon-dash-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$g();
      }
      break;
    case "wpp-icon-error-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$f();
      }
      break;
    case "wpp-icon-info-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$e();
      }
      break;
    case "wpp-icon-search-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$d();
      }
      break;
    case "wpp-icon-success-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$c();
      }
      break;
    case "wpp-icon-tick-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$b();
      }
      break;
    case "wpp-icon-warning-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$a();
      }
      break;
    case "wpp-inline-message-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$9();
      }
      break;
    case "wpp-internal-label-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$8();
      }
      break;
    case "wpp-internal-tooltip-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$7();
      }
      break;
    case "wpp-label-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$6();
      }
      break;
    case "wpp-list-item-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$5();
      }
      break;
    case "wpp-spinner-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$4();
      }
      break;
    case "wpp-tooltip-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "wpp-typography-v2-9-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}
defineCustomElement$1();

const WppAutocomplete = WppAutocomplete$1;
const defineCustomElement = defineCustomElement$1;

export { WppAutocomplete, defineCustomElement };
